import {
  Alert,
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  Skeleton,
  Stack,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import { SearchBar } from '@common/components/SearchBar';
import { useListAccessUnits } from '@apono/client-app-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';

import ResourceTypeFilter, { ResourceTypeFilterOption } from './ResourceTypeFilter';
import PermissionFilter, { PermissionFilterOption } from './PermissionFilter';
import { ComboboxOption } from '@common/ui/Combobox/Combobox';
import { FixedSizeList, ListChildComponentProps, ListOnScrollProps } from 'react-window';
import PrimarySecondary from '@common/ui/PrimarySecondary';

const MAX_TO_SELECT = 1000;

const AccessUnitItemsListWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  border: `1px solid ${theme.palette.border.default}`,
  borderRadius: theme.shape.borderRadius,
}));

type AccessUnitSelectorProps = {
  bundleIds?: string[];
  onChange: (value: ComboboxOption[] | null) => void;
  value: ComboboxOption[] | null;
  resourceTypeOptions?: ResourceTypeFilterOption[];
  isResourceTypeFetched?: boolean;
  permissionOptions?: ComboboxOption[];
  isPermissionFetched?: boolean;
};

export function AccessUnitSelector({
  value,
  onChange,
  bundleIds,
  resourceTypeOptions,
  isResourceTypeFetched,
  permissionOptions,
  isPermissionFetched,
}: AccessUnitSelectorProps) {
  const [search, setSearch] = useState('');

  const [showSelected, setShowSelected] = useState(false);
  const [selectedResourceTypes, setSelectedResourceTypes] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const { data, hasNextPage, fetchNextPage, isFetched, isFetchingNextPage } = useListAccessUnits({
    bundleIds,
    search,
    resourceTypeIds: selectedResourceTypes,
    permissionIds: selectedPermissions,
    limit: 200,
  });

  function fetchMore() {
    if (hasNextPage) fetchNextPage();
  }

  useEffect(() => {
    if (value?.length === 0) {
      setShowSelected(false);
    }
  }, [value]);

  const valuesMap = useMemo(() => new Map(value?.map((v) => [v.value, v])), [value]);

  const filterOptions = useMemo(() => {
    const resourceTypesMap = new Map<string, ResourceTypeFilterOption>();
    const permissionsMap = new Map<string, PermissionFilterOption>();
    const accessUnitsMap = new Map<string, ComboboxOption>();

    data?.pages.flatMap((page) =>
      page.data.forEach((accessUnit) => {
        const resourceType = accessUnit.resource.type;
        if (!resourceTypesMap.has(resourceType.id)) {
          resourceTypesMap.set(resourceType.id, {
            value: resourceType.id,
            label: resourceType.name,
            icon: resourceType.icons.svg,
            searchHelper: [
              accessUnit.resource.integration.name,
              accessUnit.resource.integration.type_display_name,
              resourceType.display_path,
              resourceType.name,
            ].join(' '),
          });
        }

        const permission = accessUnit.permission;
        if (!permissionsMap.has(permission.id)) {
          permissionsMap.set(permission.id, {
            value: permission.id,
            label: permission.name,
          });
        }

        const isAccessUnitSelected = showSelected && valuesMap.has(accessUnit.id);
        const isResourceTypeMatch =
          selectedResourceTypes.length === 0 || selectedResourceTypes.includes(accessUnit.resource.type.id);
        const isPermissionMatch =
          selectedPermissions.length === 0 || selectedPermissions.includes(accessUnit.permission.id);
        const isAccessUnitMatch = !showSelected && isResourceTypeMatch && isPermissionMatch;
        if (isAccessUnitSelected || isAccessUnitMatch) {
          accessUnitsMap.set(accessUnit.id, {
            label: accessUnit.resource.path,
            subLabel: accessUnit.resource.type.display_path,
            rightLabel: accessUnit.permission.name,
            value: accessUnit.id,
            icon: accessUnit.resource.type.icons.svg,
          });
        }
      }),
    );

    if (showSelected) {
      valuesMap.forEach((value) => {
        if (!accessUnitsMap.has(value.value)) {
          accessUnitsMap.set(value.value, value);
        }
      });
    }

    return {
      resourceTypes: resourceTypeOptions ?? Array.from(resourceTypesMap.values()),
      permissions: permissionOptions ?? Array.from(permissionsMap.values()),
      accessUnits: Array.from(accessUnitsMap.values()),
    };
  }, [
    data?.pages,
    showSelected,
    resourceTypeOptions,
    permissionOptions,
    valuesMap,
    selectedResourceTypes,
    selectedPermissions,
  ]);

  return (
    <Stack direction="column" spacing={2} data-testid="access-units-selector">
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <Box sx={{ flexGrow: 1 }}>
          <SearchBar placeholder="Search resources" value={search} onChange={setSearch} fullWidth />
        </Box>
        <Stack direction="row" spacing={2}>
          <ResourceTypeFilter
            options={filterOptions.resourceTypes}
            value={selectedResourceTypes}
            onChange={setSelectedResourceTypes}
            loading={!isResourceTypeFetched}
          />
          <PermissionFilter
            options={filterOptions.permissions}
            value={selectedPermissions}
            onChange={setSelectedPermissions}
            loading={!isPermissionFetched}
          />
        </Stack>
      </Stack>
      <AccessUnitItemsListWrapper>
        <AccessUnitItemsList
          options={filterOptions.accessUnits}
          valuesMap={valuesMap}
          onChange={onChange}
          showSelected={showSelected}
          setShowSelected={setShowSelected}
          loading={!isFetched}
          isLoadingMore={isFetchingNextPage}
          fetchMore={fetchMore}
        />
      </AccessUnitItemsListWrapper>
    </Stack>
  );
}

interface AccessUnitItemsListProps {
  options: ComboboxOption[];
  valuesMap: Map<string, ComboboxOption>;
  onChange: (value: ComboboxOption[]) => void;
  showSelected: boolean;
  setShowSelected: (value: boolean) => void;
  loading?: boolean;
  hasMore?: boolean;
  fetchMore?: () => void;
  isLoadingMore?: boolean;
}

const SCROLL_OFFSET = 5;
const ITEM_SIZE = 56;
const LIST_HEIGHT = 400;

function AccessUnitItemsList({
  options,
  valuesMap,
  onChange,
  showSelected,
  setShowSelected,
  loading,
  fetchMore,
  isLoadingMore,
}: AccessUnitItemsListProps) {
  const maxSelected = valuesMap.size === MAX_TO_SELECT;
  const [bottomReached, setBottomReached] = useState(false);

  const totalHeight = useMemo(() => options.length * ITEM_SIZE, [options]);
  const height = useMemo(() => Math.min(totalHeight, LIST_HEIGHT), [totalHeight]);

  useEffect(() => {
    setBottomReached(false);
  }, [options]);

  const handleOnScroll = useCallback(
    ({ scrollOffset }: ListOnScrollProps) => {
      const toBottom = totalHeight - (height + scrollOffset);
      if (totalHeight > height && toBottom < SCROLL_OFFSET * ITEM_SIZE && !bottomReached) {
        setBottomReached(true);
        fetchMore?.();
      }
    },
    [bottomReached, fetchMore, height, totalHeight],
  );

  const handleElementToSelect = useCallback(
    (newVal: ComboboxOption) => {
      const newMap = new Map(valuesMap);
      const list = newMap.has(newVal.value)
        ? newMap.delete(newVal.value)
          ? Array.from(newMap.values())
          : []
        : [...newMap.values(), newVal];
      list.slice(0, MAX_TO_SELECT);
      return list;
    },
    [valuesMap],
  );

  const handleOnChange = useCallback(
    (newVal: ComboboxOption) => {
      onChange(handleElementToSelect(newVal));
    },
    [handleElementToSelect, onChange],
  );

  const selectedOptions = useMemo(() => options.filter((option) => valuesMap.has(option.value)), [options, valuesMap]);

  const handleSelectAllOptions = useCallback(() => {
    const newMap = new Map(valuesMap);
    if (selectedOptions.length === options.length) {
      for (const option of options) {
        newMap.delete(option.value);
      }
    } else {
      for (const option of options) {
        newMap.set(option.value, option);
      }
    }
    onChange(Array.from(newMap.values()));
  }, [onChange, options, selectedOptions.length, valuesMap]);

  function renderAccessUnitItemsItem(props: ListChildComponentProps) {
    const { index, style } = props;
    const { value: optionValue, label, subLabel, rightLabel, icon } = options[index];

    return (
      <ListItem style={style} key={index} data-testid="access-units-selector-item">
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            edge="start"
            checked={valuesMap.has(optionValue)}
            size="small"
            disableRipple
            disabled={maxSelected}
            onChange={() => handleOnChange(options[index])}
          />
        </ListItemIcon>
        <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" width={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {icon && <Avatar src={icon} sx={{ width: 20, height: 20 }} />}
            <PrimarySecondary primary={label} secondary={subLabel} maxWidth={700} />
          </Stack>
          {rightLabel && (
            <Typography variant="caption" color="text.muted">
              {rightLabel}
            </Typography>
          )}
        </Stack>
      </ListItem>
    );
  }

  return (
    <Stack direction="column" data-testid="access-units-selector-list">
      <SelectBar
        totalSelected={valuesMap.size}
        selectedAll={selectedOptions.length === options.length}
        totalOptions={options.length}
        onSelectAllOptions={handleSelectAllOptions}
        showSelected={showSelected}
        onShowSelected={() => setShowSelected(!showSelected)}
        disabled={loading}
      />
      {maxSelected && (
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          You can select up to {MAX_TO_SELECT} resources at a time
        </Alert>
      )}
      {loading ? (
        <AccessUnitItemLoading />
      ) : options.length === 0 ? (
        <Stack direction="column" alignItems="center" justifyContent="center" sx={{ height: 400 }}>
          <Typography variant="body2" color="text.muted">
            No access units found
          </Typography>
        </Stack>
      ) : (
        <FixedSizeList
          height={LIST_HEIGHT}
          width="100%"
          itemSize={ITEM_SIZE}
          itemCount={options.length}
          overscanCount={5}
          onScroll={handleOnScroll}
        >
          {renderAccessUnitItemsItem}
        </FixedSizeList>
      )}
      {isLoadingMore && <LinearProgress color="inherit" sx={{ height: 2 }} />}
    </Stack>
  );
}

function AccessUnitItemLoading() {
  return (
    <List>
      {Array.from({ length: 3 }).map((_, index) => (
        <ListItem key={index} dense>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox edge="start" disableRipple disabled size="small" />
          </ListItemIcon>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between" width={1}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Skeleton variant="circular" width={20} height={20} />
              <Stack>
                <Skeleton variant="text" width={100} />
                <Skeleton variant="text" width={150} />
              </Stack>
            </Stack>
            <Skeleton variant="text" width={50} />
          </Stack>
        </ListItem>
      ))}
    </List>
  );
}

const SelectBarWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  backgroundColor: theme.palette.background.active,
  borderBottom: `1px solid ${theme.palette.border.default}`,
}));

function SelectBar({
  totalSelected,
  selectedAll,
  totalOptions,
  onSelectAllOptions,
  showSelected,
  onShowSelected,
  disabled,
}: {
  totalSelected: number;
  selectedAll: boolean;
  totalOptions: number;
  onSelectAllOptions: () => void;
  showSelected: boolean;
  onShowSelected: () => void;
  disabled?: boolean;
}) {
  return (
    <SelectBarWrapper>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={2} alignItems="center">
          <Tooltip
            title={totalOptions > MAX_TO_SELECT ? `You can select up to ${MAX_TO_SELECT} resources at a time` : ''}
            arrow
            placement="top"
          >
            <FormControlLabel
              disabled={disabled || totalOptions === 0 || totalOptions > MAX_TO_SELECT}
              control={
                <Checkbox
                  checked={selectedAll}
                  disableRipple
                  size="small"
                  sx={{ paddingTop: 0.8, paddingBottom: 0.8 }}
                />
              }
              label={
                <Typography variant="body2" color="text.muted">
                  {selectedAll ? 'Deselect' : 'Select'} all
                </Typography>
              }
              onChange={onSelectAllOptions}
            />
          </Tooltip>
        </Stack>
        {totalSelected > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip label={`Selected ${totalSelected} resource`} size="small" color="primary" />
            <FormControlLabel
              control={
                <Checkbox
                  checked={showSelected}
                  disableRipple
                  size="small"
                  sx={{ paddingTop: 0.8, paddingBottom: 0.8 }}
                  disabled={disabled}
                />
              }
              label={
                <Typography variant="body2" color="text.muted">
                  Show selected
                </Typography>
              }
              onChange={onShowSelected}
            />
          </Stack>
        )}
      </Stack>
    </SelectBarWrapper>
  );
}
